import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../styles/AdminDashboard.css';
import '../../styles/MemberDetails.css';
import api from '../../api';

// Import the same icons used in other components for consistency
import {
  AccountCircle as ProfileIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Groups as ClubsIcon,
  Event as EventsIcon,
  People as ManageMembersIcon,
  PersonAdd as AddMemberIcon,
} from '@mui/icons-material';

const normalizeStatus = (value) => {
  const raw = (value ?? '').toString().trim().toLowerCase();
  if (!raw) return 'pending';
  if (raw === 'suspended') return 'inactive';
  return raw;
};

const stripClubSuffix = (value) => {
  const text = (value ?? '').toString().trim().replace(/\s+/g, ' ');
  return text.replace(/\s*\(.*\)\s*$/, '').trim();
};

const ViewMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [member, setMember] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  // Editable fields
  const [clubId, setClubId] = useState('');
  const [uniqueCode, setUniqueCode] = useState('');
  const [status, setStatus] = useState('pending');
  const mainContentRef = useRef(null);
  const [menuWidths, setMenuWidths] = useState({ club: null, status: null });
  const [openSelectField, setOpenSelectField] = useState(null);

  // Needed for syncing Club.members JSON
  const [initialClubId, setInitialClubId] = useState(null);

  const generateUniqueCode = () => {
    // Short, human-friendly code. (No backend call; persisted on Save.)
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const length = 8;
    let out = '';
    for (let i = 0; i < length; i += 1) {
      out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    setUniqueCode(out);
    setToast({ open: true, severity: 'info', message: 'Unique code generated. Click Save to persist.' });
  };

  const sidebarItems = [
    {
      label: 'Profile',
      path: '/admin/profile',
      action: () => navigate('/admin/profile'),
      icon: <ProfileIcon />,
    },
    {
      label: 'Manage Clubs',
      path: '/admin/clubs',
      action: () => navigate('/admin/clubs'),
      icon: <ClubsIcon />,
    },
    {
      label: 'Manage Members',
      path: '/admin/memberships',
      action: () => navigate('/admin/memberships'),
      icon: <ManageMembersIcon />,
    },
    {
      label: 'Add Member',
      path: '/admin/add-member',
      action: () => navigate('/admin/add-member'),
      icon: <AddMemberIcon />,
    },
    {
      label: 'Manage Events',
      path: '/admin/events',
      action: () => navigate('/admin/events'),
      icon: <EventsIcon />,
    },
    {
      label: 'Lost and Found',
      path: '/lostfound',
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />,
    },
    {
      label: 'Skills',
      path: '/skills',
      action: () => navigate('/skills'),
      icon: <SkillsIcon />,
    },
    {
      label: 'Doubts',
      path: '/yourdoubts',
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />,
    },
    {
      label: 'Settings',
      path: '/settings',
      action: () => navigate('/settings'),
      icon: <SettingsIcon />,
    },
    {
      label: 'Help',
      path: '/help',
      action: () => navigate('/help'),
      icon: <DoubtsIcon />,
    },
  ];

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const userId = Number(id);
        // Preferred: club-members endpoint includes club + membership status.
        const resp = await api.get('users/club-members/');
        const payload = resp?.data ?? resp;
        const rows = Array.isArray(payload)
          ? payload
          : (payload?.data ?? payload?.results?.data ?? payload?.results ?? payload?.data ?? []);
        const list = Array.isArray(rows) ? rows : [];

        const row = list.find((r) => Number(r?.user_id ?? r?.id) === userId);
        if (!row) {
          navigate('/admin/memberships');
          return;
        }

        const rowId = (row?.row_id ?? '').toString();
        const parsedClubId = Number.isFinite(Number(row?.club_id))
          ? Number(row?.club_id)
          : (rowId.includes(':') ? Number(rowId.split(':')[0]) : null);
        const clubLabel = stripClubSuffix(row?.club ?? '');
        const normalizedStatus = normalizeStatus(row?.status);

        // Membership unique code is stored in Club.members[].member_id (see AddMember flow).
        // For some rows we may also have a user-level club_member_id.
        const resolvedUniqueCode =
          row?.membership_member_id ??
          row?.member_id ??
          row?.club_member_id ??
          row?.clubMemberId ??
          row?.club_member_code ??
          row?.uniqueCode ??
          row?.unique_code ??
          '';

        const resolvedStudentId =
          row?.student_id ??
          row?.studentId ??
          row?.member_id ??
          '';

        setMember({
          id: userId,
          name: row?.username ?? row?.name ?? '—',
          email: row?.email ?? '—',
          studentId: resolvedStudentId ?? '',
          yearOfStudy: row?.year ?? '',
          club: clubLabel,
          uniqueCode: (resolvedUniqueCode ?? '').toString(),
          college: row?.college ?? '',
          department: row?.department ?? '',
          status: normalizedStatus,
        });

        setUniqueCode((resolvedUniqueCode ?? '').toString());
        setStatus(normalizedStatus);
        setInitialClubId(Number.isFinite(parsedClubId) ? parsedClubId : null);
        setClubId(Number.isFinite(parsedClubId) ? parsedClubId : '');
      } catch (_err) {
        navigate('/admin/memberships');
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [id, navigate]);

  const handleBack = () => {
    const stateReturnTo = location?.state?.returnTo;
    const qp = new URLSearchParams(location?.search || '');
    const queryReturnTo = qp.get('returnTo');
    const candidate = (stateReturnTo || queryReturnTo || '').toString();

    // Only allow returning to Manage Members screens.
    if (candidate.startsWith('/admin/memberships')) {
      navigate(candidate);
      return;
    }
    navigate('/admin/memberships');
  };

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const resp = await api.get('events/club-create-view/');
        const payload = resp?.data ?? resp;
        const clubsData = Array.isArray(payload)
          ? payload
          : (payload?.results?.data ?? payload?.results ?? payload?.data ?? []);
        const list = Array.isArray(clubsData) ? clubsData : [];
        setClubs(
          list
            .map((c) => ({ id: c?.id, name: (c?.name ?? '').toString().trim() }))
            .filter((c) => c.id && c.name)
        );
      } catch (_err) {
        setClubs([]);
      }
    };
    fetchClubs();
  }, []);

  const clubOptions = useMemo(() => {
    const opts = Array.isArray(clubs) ? clubs.slice() : [];
    if (clubId && !opts.some((c) => String(c.id) === String(clubId))) {
      opts.unshift({ id: clubId, name: member?.club || 'Current club' });
    }
    return opts;
  }, [clubs, clubId, member?.club]);

  useEffect(() => {
    // If we couldn't parse initial club id (row_id missing), infer from the club name.
    if (initialClubId != null) return;
    const currentLabel = stripClubSuffix(member?.club ?? '');
    if (!currentLabel) return;
    const match = (Array.isArray(clubs) ? clubs : []).find(
      (c) => stripClubSuffix(c?.name) === currentLabel
    );
    if (match?.id != null) {
      setInitialClubId(Number(match.id));
      if (!clubId) setClubId(Number(match.id));
    }
  }, [clubs, member?.club, initialClubId, clubId]);

  const persistChanges = async ({ nextStatus } = {}) => {
    if (!member?.id) return;

    const selectedClubId = Number(clubId);
    if (!Number.isFinite(selectedClubId)) {
      setToast({ open: true, severity: 'error', message: 'Club selection is required.' });
      return;
    }

    const desiredStatus = normalizeStatus(nextStatus ?? status);
    const emailNorm = (member?.email ?? '').toString().trim();
    if (!emailNorm || emailNorm === '—') {
      setToast({ open: true, severity: 'error', message: 'Missing email for this member.' });
      return;
    }

    setSaving(true);
    try {
      // 1) Update user record
      await api.patch(`users/update-user/${member.id}/`, {
        status: desiredStatus,
        club: selectedClubId,
        // Note: backend only applies club_member_id for role=club_member.
        club_member_id: (uniqueCode ?? '').toString(),
      });

      // 2) Sync Club.members JSON
      const membershipCode = (uniqueCode ?? '').toString().trim();

      if (initialClubId && initialClubId !== selectedClubId) {
        const removePayload = {
          action: 'remove',
          email: emailNorm,
          ...(membershipCode ? { member_id: membershipCode } : {}),
        };
        await api.patch(`events/clubmembers/${initialClubId}/`, removePayload);
      }

      const upsertPayload = {
        action: 'upsert',
        email: emailNorm,
        status: desiredStatus,
        ...(membershipCode ? { member_id: membershipCode } : {}),
      };
      await api.patch(`events/clubmembers/${selectedClubId}/`, upsertPayload);

      const clubName = clubOptions.find((c) => String(c.id) === String(selectedClubId))?.name;
      setMember((prev) =>
        prev
          ? {
              ...prev,
              uniqueCode,
              status: desiredStatus,
              club: clubName ? stripClubSuffix(clubName) : prev.club,
            }
          : prev
      );
      setInitialClubId(selectedClubId);
      setClubId(selectedClubId);
      setStatus(desiredStatus);
      setToast({ open: true, severity: 'success', message: 'Saved successfully.' });
    } catch (_err) {
      setToast({ open: true, severity: 'error', message: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard member-details">
        <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
        <div className="dashboard-layout">
          {!isMobile && <GlobalSidebar />}
          <main className="dashboard-main">
            <div className="dashboard-container">
              <Skeleton height={56} sx={{ borderRadius: 2 }} />
              <Skeleton height={480} sx={{ mt: 2, borderRadius: 2 }} />
            </div>
          </main>
        </div>
        <PostLoginFooter />
      </div>
    );
  }

  if (!member) return null;

  const statusChip = (value) => {
    const normalized = normalizeStatus(value);
    if (normalized === 'active') return { label: 'Active', color: 'success' };
    if (normalized === 'pending') return { label: 'Pending', color: 'warning' };
    if (normalized === 'inactive') return { label: 'Inactive', color: 'error' };
    return { label: normalized || 'Unknown', color: 'default' };
  };

  const currentChip = statusChip(member.status);

  const handleSelectOpen = (field) => (event) => {
    const width = event.currentTarget?.getBoundingClientRect().width;
    if (width) {
      setMenuWidths((prev) => ({ ...prev, [field]: width }));
    }
    setOpenSelectField(field);
  };

  const handleSelectClose = () => {
    setOpenSelectField(null);
  };

  const getMenuProps = (field) => {
    const width = menuWidths[field];
    return {
      disablePortal: false,
      disableScrollLock: true,
      container: mainContentRef.current,
      sx: {
        zIndex: 1300,
      },
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
      marginThreshold: null,
      transitionDuration: 120,
      PaperProps: {
        sx: {
          mt: 0,
          width: width || undefined,
          minWidth: width || undefined,
          maxWidth: width || undefined,
        },
      },
    };
  };

  return (
    <div className="admin-dashboard member-details">
      <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />
      <div className="dashboard-layout">
        {!isMobile && <GlobalSidebar />}
        <main className="dashboard-main">
          <Container ref={mainContentRef} maxWidth="xl" className="dashboard-container">
            <header className="md-page-header">
              <Box className="md-header-left">
                <Box className="md-breadcrumb-row">
                  <Tooltip title="Back">
                    <IconButton onClick={handleBack} className="md-back-icon" size="medium" aria-label="Back">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Breadcrumbs
                    className="md-breadcrumbs"
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/dashboard');
                      }}
                      href="/admin/dashboard"
                      className="md-breadcrumb-link"
                    >
                      Dashboard
                    </Link>
                    <Link
                      underline="hover"
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/memberships');
                      }}
                      href="/admin/memberships"
                      className="md-breadcrumb-link"
                    >
                      Club Members
                    </Link>
                    <Typography color="text.primary" className="md-breadcrumb-current">
                      Member Details
                    </Typography>
                  </Breadcrumbs>
                </Box>

                <Box className="md-title-wrap">
                  <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="md-page-title">
                    Member Details
                  </Typography>
                  <Typography variant="body2" className="md-page-subtitle" color="text.secondary">
                    Review member info and manage membership status.
                  </Typography>
                </Box>
              </Box>

              <Box className="md-header-actions">
                <Button
                  variant="outlined"
                  onClick={() => persistChanges({ nextStatus: 'active' })}
                  disabled={saving}
                  className="md-btn-approve"
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => persistChanges({ nextStatus: 'inactive' })}
                  disabled={saving}
                  className="md-btn-suspend"
                >
                  Suspend
                </Button>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={() => persistChanges()}
                  className="md-btn-primary"
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </header>

            <Paper className="md-card" elevation={0}>
              <Box className="md-card-header">
                <Box>
                  <Typography variant="h6" className="md-card-title">
                    Member Profile
                  </Typography>
                  <Typography className="md-card-subtitle">
                    {member?.name ? `Editing membership for ${member.name}` : 'Editing membership'}
                  </Typography>
                </Box>
                <Chip
                  label={currentChip.label}
                  size="small"
                  color={currentChip.color}
                  sx={{ fontWeight: 900 }}
                />
              </Box>

              <Box className="md-form">
                <TextField
                  label="Name"
                  value={member.name}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  label="Email"
                  value={member.email}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  label="Student ID"
                  value={member.studentId || ''}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  label="Year of Study"
                  value={member.yearOfStudy || ''}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  select
                  label="Club"
                  fullWidth
                  value={clubId || ''}
                  disabled={saving}
                  onChange={(e) => setClubId(e.target.value)}
                  helperText="Editable"
                  sx={{ minWidth: 160 }}
                  SelectProps={{
                    MenuProps: getMenuProps('club'),
                    onOpen: handleSelectOpen('club'),
                    onClose: handleSelectClose,
                    open: openSelectField === 'club',
                  }}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                >
                  {clubOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Unique Code"
                  value={uniqueCode}
                  disabled={saving}
                  onChange={(e) => setUniqueCode(e.target.value)}
                  helperText="Editable"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="text"
                          size="small"
                          onClick={generateUniqueCode}
                          disabled={saving}
                          sx={{ textTransform: 'none', fontWeight: 900, px: 1 }}
                        >
                          Generate
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="College"
                  value={member.college || ''}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  label="Department"
                  value={member.department || ''}
                  disabled
                  helperText="Read only"
                />

                <TextField
                  select
                  label="Status"
                  fullWidth
                  value={status}
                  disabled={saving}
                  onChange={(e) => setStatus(e.target.value)}
                  helperText="Editable"
                  sx={{ minWidth: 160 }}
                  SelectProps={{
                    MenuProps: getMenuProps('status'),
                    onOpen: handleSelectOpen('status'),
                    onClose: handleSelectClose,
                    open: openSelectField === 'status',
                  }}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>

              </Box>

              <Divider />
              <Box className="md-form-actions">
                <Typography variant="body2" className="md-muted">
                  Fields marked “Editable” can be changed.
                </Typography>
                <Button variant="outlined" onClick={handleBack} sx={{ textTransform: 'none', fontWeight: 900 }}>
                  Back
                </Button>
              </Box>
            </Paper>
          </Container>
        </main>
      </div>
      <PostLoginFooter />

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ fontWeight: 800 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ViewMember;
